import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, ButtonGroup, Button, Table, Badge } from 'react-bootstrap';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  FaUsers, FaChartLine, FaClock, FaLayerGroup, FaChartPie, FaHistory
} from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdminGuard } from '../../../hooks/useAdminGuard';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const ActivityDashboard = () => {
  useAdminGuard(3); // Requiere AdminLevel 3

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboardData = async (days = 7) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/activity-logs/dashboard?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Dashboard API Response:', data);
      setDashboardData(data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Error al cargar el dashboard de actividades');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(timeRange);
  }, [timeRange]);

  const COLORS = ['#667eea', '#00D9FF', '#FEB019', '#00E396', '#FF4560'];

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const totalAcciones = dashboardData?.userMetrics?.total_acciones || 0;
  const usuariosActivos = dashboardData?.userMetrics?.usuarios_unicos || 0;
  const promedioAcciones = dashboardData?.userMetrics?.promedio_acciones_por_usuario || 0;

  // Preparar datos de horarios
  const horariosData = dashboardData?.activityByHour?.map(h => ({
    hora: `${h.hora}:00`,
    total: h.total
  })) || [];

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Row className="mb-4">
        <Col md={8}>
          <h2 className="mb-2" style={{ color: '#2c3e50', fontWeight: '600' }}>
            <FaChartLine className="me-3" style={{ color: '#667eea' }} />
            Dashboard de Actividades
          </h2>
          <p className="text-muted mb-0">Monitoreo y análisis de actividad del sistema</p>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <ButtonGroup>
            <Button 
              variant={timeRange === '7' ? 'primary' : 'outline-secondary'}
              onClick={() => setTimeRange('7')}
              style={{ fontWeight: timeRange === '7' ? '600' : '400' }}
            >
              7 días
            </Button>
            <Button 
              variant={timeRange === '15' ? 'primary' : 'outline-secondary'}
              onClick={() => setTimeRange('15')}
              style={{ fontWeight: timeRange === '15' ? '600' : '400' }}
            >
              15 días
            </Button>
            <Button 
              variant={timeRange === '30' ? 'primary' : 'outline-secondary'}
              onClick={() => setTimeRange('30')}
              style={{ fontWeight: timeRange === '30' ? '600' : '400' }}
            >
              30 días
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #667eea' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Total Acciones
                  </p>
                  <h2 className="mb-0" style={{ color: '#667eea', fontWeight: '700' }}>
                    {totalAcciones.toLocaleString()}
                  </h2>
                  <small className="text-muted">últimos {timeRange} días</small>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(102, 126, 234, 0.1)' }}>
                  <FaChartLine size={28} style={{ color: '#667eea' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #00D9FF' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Usuarios Únicos
                  </p>
                  <h2 className="mb-0" style={{ color: '#00D9FF', fontWeight: '700' }}>
                    {usuariosActivos}
                  </h2>
                  <small className="text-muted">en período</small>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(0, 217, 255, 0.1)' }}>
                  <FaUsers size={28} style={{ color: '#00D9FF' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #FEB019' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Promedio Acciones
                  </p>
                  <h2 className="mb-0" style={{ color: '#FEB019', fontWeight: '700' }}>
                    {promedioAcciones}
                  </h2>
                  <small className="text-muted">por usuario</small>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(254, 176, 25, 0.1)' }}>
                  <FaChartPie size={28} style={{ color: '#FEB019' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #00E396' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Hora Pico
                  </p>
                  <h2 className="mb-0" style={{ color: '#00E396', fontWeight: '700' }}>
                    {horariosData.length > 0 ? horariosData.reduce((max, h) => h.total > max.total ? h : max).hora : '-'}
                  </h2>
                  <small className="text-muted">mayor actividad</small>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(0, 227, 150, 0.1)' }}>
                  <FaClock size={28} style={{ color: '#00E396' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos principales */}
      <Row className="mb-4">
        {/* Actividad diaria */}
        <Col lg={8} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaChartLine className="me-2 text-primary" />
                Tendencia de Actividad
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '1rem' }}>
              {dashboardData?.activityByDay && dashboardData.activityByDay.length > 0 ? (
                <div style={{ width: '100%', height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboardData.activityByDay} margin={{ top: 15, right: 30, left: 10, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="fecha" 
                        tickFormatter={(date) => format(new Date(date), 'dd/MM', { locale: es })}
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis style={{ fontSize: '12px' }} />
                      <Tooltip 
                        labelFormatter={(date) => format(new Date(date), 'dd MMMM yyyy', { locale: es })}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '10px' }} />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#667eea" 
                        strokeWidth={3}
                        name="Actividades"
                        dot={{ fill: '#667eea', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartLine size={40} className="mb-3 opacity-25" />
                  <p>No hay datos de actividad en este período</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Categorías de acciones */}
        <Col lg={4} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaLayerGroup className="me-2 text-primary" />
                Por Categoría
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '1rem', overflow: 'hidden', height: '350px' }}>
              {dashboardData?.actionCategories && dashboardData.actionCategories.length > 0 ? (
                <div style={{ width: '100%', height: '100%' }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={dashboardData.actionCategories}
                        dataKey="total"
                        nameKey="categoria"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#ccc', strokeWidth: 1 }}
                      >
                        {dashboardData.actionCategories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }}
                        formatter={(value, name) => [`${value} acciones`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartPie size={40} className="mb-3 opacity-25" />
                  <p>No hay categorías disponibles</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actividad por hora */}
      <Row className="mb-4">
        <Col lg={12} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaClock className="me-2 text-primary" />
                Actividad por Hora del Día
              </h5>
            </Card.Header>
            <Card.Body style={{ overflow: 'hidden' }}>
              {horariosData && horariosData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={horariosData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="hora" style={{ fontSize: '12px' }} />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }} />
                    <Bar dataKey="total" fill="#00D9FF" name="Acciones" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaClock size={40} className="mb-3 opacity-25" />
                  <p>No hay datos de actividad por hora</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usuarios más activos y Top acciones */}
      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaUsers className="me-2 text-primary" />
                Top 10 Usuarios Más Activos
              </h5>
            </Card.Header>
            <Card.Body style={{ overflow: 'hidden' }}>
              {dashboardData?.topUsuarios && dashboardData.topUsuarios.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={dashboardData.topUsuarios} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis type="number" style={{ fontSize: '12px' }} />
                    <YAxis 
                      dataKey="nombre" 
                      type="category" 
                      width={120} 
                      style={{ fontSize: '11px' }}
                    />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }} />
                    <Bar dataKey="total_acciones" fill="#667eea" name="Acciones" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaUsers size={40} className="mb-3 opacity-25" />
                  <p>No hay datos de usuarios</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaChartLine className="me-2 text-primary" />
                Top 10 Acciones Más Frecuentes
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '410px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th>Acción</th>
                    <th className="text-end" style={{ width: '100px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.topAcciones && dashboardData.topAcciones.length > 0 ? (
                    dashboardData.topAcciones.map((accion, idx) => (
                      <tr key={idx}>
                        <td><Badge bg="primary" pill>{idx + 1}</Badge></td>
                        <td>{accion.accion}</td>
                        <td className="text-end">
                          <span className="badge bg-info">{accion.total}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-4">
                        No hay datos de acciones
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Últimas Actividades Detalladas - Simplificado */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaHistory className="me-2 text-primary" />
                Últimas 50 Actividades
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table hover responsive size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '100px' }}>Legajo</th>
                    <th style={{ width: '200px' }}>Nombre</th>
                    <th>Acción</th>
                    <th style={{ width: '160px' }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.ultimasActividades && dashboardData.ultimasActividades.length > 0 ? (
                    dashboardData.ultimasActividades.map((log, idx) => (
                      <tr key={idx}>
                        <td><strong>{log.user_legajo}</strong></td>
                        <td>{log.usuario_nombre || `Legajo ${log.user_legajo}`}</td>
                        <td>
                          <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {log.accion || log.action}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {format(new Date(log.created_at), 'dd/MM/yy HH:mm', { locale: es })}
                          </small>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        <FaHistory size={40} className="mb-3 opacity-25" />
                        <p className="mb-0">No hay actividades registradas en este período</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ActivityDashboard;
